import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import BrandLockup from '../BrandLockup';
import {
  SignOut,
  Trash,
  SpinnerGap,
  Plus,
  Image as ImageIcon,
  Envelope,
  Phone,
  WhatsappLogo,
  CheckCircle,
  ArrowCounterClockwise,
  MagnifyingGlass,
  ChatCircleDots,
  Buildings,
  GraduationCap,
  Eye,
  EyeSlash,
  Tag,
  Scroll,
  Handshake
} from '@phosphor-icons/react';
import { adminAuthAPI, adminEnquiriesAPI, adminPartnersAPI } from '../../services/api';
import AdminInterns from './AdminInterns';
import AdminInternDetail from './AdminInternDetail';

const PAGES = ['interns', 'plans', 'formats', 'enquiries', 'freelancers', 'partners', 'certificate'];

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'new', label: 'New' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'service_page', label: 'Service pages' },
  { id: 'website', label: 'Home / Contact' },
];

const sourceMeta = (item) => {
  const type = item.source_type || 'general';
  if (type === 'service_page') {
    return {
      label: item.source_label || 'Service page',
      hint: 'Came from a specific service page',
      className: 'bg-brand-50 text-brand-700 border-brand-100'
    };
  }
  if (type === 'contact_page') {
    return {
      label: item.source_label || 'Contact page',
      hint: 'Came from the Contact Us page',
      className: 'bg-sky-50 text-sky-700 border-sky-100'
    };
  }
  if (type === 'partner_register') {
    return {
      label: item.source_label || 'Freelancer / sell',
      hint: 'Came from homepage partner registration',
      className: 'bg-violet-50 text-violet-700 border-violet-100'
    };
  }
  return {
    label: item.source_label || 'Home / website',
    hint: 'Came from the home page enquiry form',
    className: 'bg-slate-100 text-slate-700 border-slate-200'
  };
};

const digitsPhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('91') && digits.length >= 12) return digits;
  if (digits.length === 10) return `91${digits}`;
  return digits;
};

const whatsappHref = (item) => {
  const n = digitsPhone(item.phone);
  const text = `Hi ${item.name}, thank you for your enquiry about ${item.service_name} at Growmore IT Services. How can we help you further?`;
  return `https://wa.me/${n}?text=${encodeURIComponent(text)}`;
};

const mailHref = (item) => {
  const subject = `Re: ${item.service_name} enquiry — Growmore IT Services`;
  const body = `Hi ${item.name},\n\nThank you for contacting Growmore IT Services regarding ${item.service_name}.\n\nWe received your message:\n"${item.message}"\n\n`;
  return `mailto:${item.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const formatWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return iso;
  }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { page: pageParam, internId } = useParams();
  const isOfflineCerts = internId === 'certificate' || /\/admin\/interns\/certificate\/?$/.test(location.pathname);
  const page = isOfflineCerts ? 'certificate' : (internId ? 'interns' : pageParam);
  const [enquiries, setEnquiries] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [partnerError, setPartnerError] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [form, setForm] = useState({ name: '', link: '', logo: null });

  const loadEnquiries = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await adminEnquiriesAPI.list();
      setEnquiries(data.enquiries || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load enquiries.');
    } finally {
      setLoading(false);
    }
  };

  const loadPartners = async () => {
    setLoading(true);
    setPartnerError('');
    try {
      const { data } = await adminPartnersAPI.list();
      setPartners(data.partners || []);
    } catch (err) {
      setPartnerError(err.response?.data?.message || 'Could not load partners.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (page === 'enquiries' || page === 'freelancers') loadEnquiries();
    if (page === 'partners') loadPartners();
  }, [page]);

  const logout = async () => {
    try {
      await adminAuthAPI.logout();
    } catch (e) {
      /* ignore */
    }
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const removeEnquiry = async (id) => {
    if (!window.confirm('Delete this enquiry?')) return;
    await adminEnquiriesAPI.remove(id);
    setEnquiries((prev) => prev.filter((item) => item.id !== id));
  };

  const setEnquiryStatus = async (item, status) => {
    const { data } = await adminEnquiriesAPI.updateStatus(item.id, status);
    setEnquiries((prev) => prev.map((row) => (row.id === item.id ? data.enquiry : row)));
  };

  const addPartner = async (event) => {
    event.preventDefault();
    setSaving(true);
    setPartnerError('');
    try {
      const data = new FormData();
      data.append('name', form.name);
      if (form.link) data.append('link', form.link);
      if (form.logo) data.append('logo', form.logo);
      const { data: res } = await adminPartnersAPI.create(data);
      setPartners((prev) => [...prev, res.partner]);
      setForm({ name: '', link: '', logo: null });
      event.target.reset();
    } catch (err) {
      setPartnerError(err.response?.data?.message || 'Could not add partner.');
    } finally {
      setSaving(false);
    }
  };

  const removePartner = async (id) => {
    if (!window.confirm('Remove this partner from the website?')) return;
    await adminPartnersAPI.remove(id);
    setPartners((prev) => prev.filter((item) => item.id !== id));
  };

  const togglePartner = async (item) => {
    const { data } = await adminPartnersAPI.toggle(item.id);
    setPartners((prev) => prev.map((row) => (row.id === item.id ? data.partner : row)));
  };

  const stats = useMemo(() => {
    const newCount = enquiries.filter((item) => (item.status || 'new') !== 'resolved').length;
    const resolved = enquiries.filter((item) => item.status === 'resolved').length;
    return { total: enquiries.length, newCount, resolved };
  }, [enquiries]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return enquiries.filter((item) => {
      const status = item.status || 'new';
      const type = item.source_type || 'general';
      if (filter === 'new' && status === 'resolved') return false;
      if (filter === 'resolved' && status !== 'resolved') return false;
      if (filter === 'service_page' && type !== 'service_page') return false;
      if (filter === 'website' && (type === 'service_page' || type === 'partner_register')) return false;
      if (!q) return true;
      return [item.name, item.email, item.phone, item.company, item.service_name, item.source_label, item.message]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [enquiries, filter, query]);

  const NAV = [
    { id: 'interns', label: 'Interns', icon: GraduationCap, to: '/admin/interns/certificate' },
    { id: 'plans', label: 'Plans', icon: Tag },
    { id: 'formats', label: 'Format', icon: Scroll },
    { id: 'enquiries', label: 'Enquiries', icon: ChatCircleDots },
    { id: 'freelancers', label: 'Freelancers', icon: Handshake },
    { id: 'partners', label: 'Partners', icon: Buildings }
  ];

  const partnerRegs = useMemo(
    () => enquiries.filter((item) => (item.source_type || '') === 'partner_register'),
    [enquiries]
  );

  if (!PAGES.includes(page)) {
    return <Navigate to="/admin/interns" replace />;
  }

  const headerTitle = internId && !isOfflineCerts
    ? 'Intern'
    : page === 'certificate'
      ? 'Interns'
      : page;

  const isNavActive = (id) => {
    if (id === 'interns') {
      return location.pathname === '/admin/interns' || location.pathname.startsWith('/admin/interns/');
    }
    return location.pathname === `/admin/${id}` || location.pathname.startsWith(`/admin/${id}/`);
  };

  const navClass = (active) =>
    `w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-left ${
      active ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-slate-50'
    }`;

  return (
    <div className={`${page === 'formats' ? 'h-[100dvh] overflow-hidden' : 'min-h-[100dvh]'} bg-slate-50 flex [&_.input-field]:!bg-white [&_.input-field]:!text-gray-900 [&_.input-field]:!border-gray-200`}>
      <aside className="hidden md:flex w-56 shrink-0 flex-col bg-white border-r border-gray-100">
        <div className="px-4 py-5 border-b border-gray-100">
          <BrandLockup size="sm" />
        </div>
        <nav className="p-3 flex-1 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = isNavActive(item.id);
            return (
              <NavLink
                key={item.id}
                to={item.to || `/admin/${item.id}`}
                className={navClass(active)}
              >
                <Icon className="w-5 h-5" weight={active ? 'fill' : 'duotone'} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-gray-100 space-y-1">
          <Link to="/" className="block px-3 py-2 text-sm font-semibold text-brand-600">Website</Link>
          <button type="button" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-red-500">
            <SignOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <div className={`flex-1 min-w-0 ${page === 'formats' ? 'flex flex-col min-h-0 overflow-hidden' : ''}`}>
      <header className="bg-white/95 backdrop-blur border-b border-gray-100 sticky top-0 z-20 shrink-0 pt-[env(safe-area-inset-top)]">
        <div className="px-3 md:px-4 h-12 md:h-auto md:py-3 flex items-center justify-between gap-3">
          <div className="md:hidden min-w-0"><BrandLockup size="sm" /></div>
          <p className="text-sm font-semibold text-gray-700 capitalize truncate">{headerTitle}</p>
          <button type="button" onClick={logout} className="md:hidden p-2 -mr-1 text-red-500" aria-label="Logout">
            <SignOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className={`px-3 md:px-4 ${page === 'formats' ? 'py-3 flex-1 min-h-0 overflow-hidden flex flex-col' : 'py-3 md:py-6 pb-24 md:pb-6'} ${page === 'formats' || page === 'freelancers' ? 'max-w-[96rem]' : 'max-w-6xl'}`}>
        {isOfflineCerts && <AdminInterns section="certificate" />}
        {internId && !isOfflineCerts && <AdminInternDetail />}
        {!internId && (page === 'interns' || page === 'plans' || page === 'formats') && <AdminInterns section={page} />}

        {page === 'enquiries' ? (
        <section>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full badge-primary mb-3">
                <ChatCircleDots className="w-4 h-4 text-brand-600" weight="fill" />
                <span className="text-xs font-semibold uppercase tracking-wide">Enquiries</span>
              </div>
              <h1 className="text-display text-3xl font-extrabold text-gray-900">
                Client <span className="text-gradient">enquiries</span>
              </h1>
              <p className="text-sm text-gray-500 mt-1">See which page they came from, reply by mail or WhatsApp, and mark resolved.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Total', value: stats.total, icon: Buildings, tone: 'normal' },
              { label: 'New', value: stats.newCount, icon: ChatCircleDots, tone: 'new' },
              { label: 'Resolved', value: stats.resolved, icon: CheckCircle, tone: 'normal' }
            ].map((card) => {
              const Icon = card.icon;
              const isHot = card.tone === 'new' && card.value > 0;
              return (
                <div
                  key={card.label}
                  className={`rounded-2xl border shadow-soft p-4 ${
                    isHot
                      ? 'bg-red-50 border-red-200'
                      : 'bg-white border-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className={`text-xs font-semibold uppercase tracking-wide ${isHot ? 'text-red-500' : 'text-gray-400'}`}>{card.label}</p>
                    <Icon className={`w-5 h-5 ${isHot ? 'text-red-600' : 'text-brand-600'}`} weight="duotone" />
                  </div>
                  <p className={`text-display text-3xl font-extrabold ${isHot ? 'text-red-700' : 'text-gray-900'}`}>{card.value}</p>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col lg:flex-row gap-3 mb-6">
            <div className="flex flex-wrap gap-2 flex-1">
              {FILTERS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setFilter(item.id)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border smooth-transition ${
                    filter === item.id
                      ? 'bg-brand-600 text-white border-brand-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-brand-200 hover:text-brand-600'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <label className="relative lg:w-72">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="input-field pl-10"
                placeholder="Search name, phone, service..."
              />
            </label>
          </div>

          {loading && (
            <p className="flex items-center gap-2 text-gray-500">
              <SpinnerGap className="w-5 h-5 animate-spin" /> Loading...
            </p>
          )}
          {error && <p className="text-red-500 mb-4">{error}</p>}

          {!loading && filtered.length === 0 && (
            <div className="rounded-3xl bg-white border border-gray-100 p-10 text-center text-gray-500">
              No enquiries in this view.
            </div>
          )}

          <div className="space-y-4">
            {filtered.map((item) => {
              const source = sourceMeta(item);
              const resolved = item.status === 'resolved';
              return (
                <article
                  key={item.id}
                  className={`rounded-3xl border shadow-soft p-5 sm:p-6 ${
                    resolved
                      ? 'bg-white border-emerald-100'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div>
                      <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${resolved ? 'text-brand-600' : 'text-red-600'}`}>{item.service_name}</p>
                      <h3 className="font-display text-xl font-extrabold text-gray-900">{item.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{item.phone} · {item.email}</p>
                      {item.company && <p className="text-sm text-gray-500">{item.company}</p>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${source.className}`}>
                        {source.label}
                      </span>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                        resolved ? 'bg-emerald-50 text-emerald-700' : 'bg-red-600 text-white'
                      }`}>
                        {resolved ? 'Resolved' : 'New'}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 mb-3">{source.hint}{item.source_page ? ` · ${item.source_page}` : ''}</p>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{item.message}</p>
                  <p className="text-xs text-gray-400 mt-3">{formatWhen(item.created_at)}</p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    <a href={mailHref(item)} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white text-brand-700 text-sm font-semibold hover:bg-brand-50 border border-brand-100">
                      <Envelope className="w-4 h-4" weight="fill" /> Email
                    </a>
                    <a href={whatsappHref(item)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100">
                      <WhatsappLogo className="w-4 h-4" weight="fill" /> WhatsApp
                    </a>
                    <a href={`tel:${item.phone}`} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-sky-50 text-sky-700 text-sm font-semibold hover:bg-sky-100">
                      <Phone className="w-4 h-4" weight="fill" /> Call
                    </a>
                    {resolved ? (
                      <button type="button" onClick={() => setEnquiryStatus(item, 'new')} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 text-amber-700 text-sm font-semibold hover:bg-amber-100">
                        <ArrowCounterClockwise className="w-4 h-4" /> Reopen
                      </button>
                    ) : (
                      <button type="button" onClick={() => setEnquiryStatus(item, 'resolved')} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
                        <CheckCircle className="w-4 h-4" weight="fill" /> Mark resolved
                      </button>
                    )}
                    <button type="button" onClick={() => removeEnquiry(item.id)} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-red-500 text-sm font-semibold hover:bg-red-100 ml-auto">
                      <Trash className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
        ) : null}

        {page === 'freelancers' ? (
        <section className="w-full">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full badge-primary mb-3">
              <Handshake className="w-4 h-4 text-brand-600" weight="fill" />
              <span className="text-xs font-semibold uppercase tracking-wide">Partner registrations</span>
            </div>
            <h1 className="text-display text-3xl font-extrabold text-gray-900">
              Freelancer <span className="text-gradient">& sell</span>
            </h1>
            <p className="text-sm text-gray-500 mt-1">People who registered from the homepage Partner with us form.</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Total', value: partnerRegs.length },
              { label: 'New', value: partnerRegs.filter((item) => (item.status || 'new') !== 'resolved').length },
              { label: 'Resolved', value: partnerRegs.filter((item) => item.status === 'resolved').length }
            ].map((card) => (
              <div key={card.label} className="rounded-2xl border border-gray-100 bg-white shadow-soft p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{card.label}</p>
                <p className="text-display text-3xl font-extrabold text-gray-900 mt-1">{card.value}</p>
              </div>
            ))}
          </div>

          {loading && (
            <p className="flex items-center gap-2 text-gray-500">
              <SpinnerGap className="w-5 h-5 animate-spin" /> Loading...
            </p>
          )}
          {error && <p className="text-red-500 mb-4">{error}</p>}

          {!loading && partnerRegs.length === 0 && (
            <div className="rounded-3xl bg-white border border-gray-100 p-10 text-center text-gray-500">
              No freelancer or sell registrations yet.
            </div>
          )}

          <div className="overflow-x-auto rounded-3xl bg-white border border-gray-100 shadow-soft">
            <table className="w-full text-sm min-w-[980px]">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Number</th>
                  <th className="px-4 py-3 font-semibold">Mail</th>
                  <th className="px-4 py-3 font-semibold">Address</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Written</th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partnerRegs.map((item) => {
                  const resolved = item.status === 'resolved';
                  const written = String(item.message || '').replace(/^Type:\s*.+\n+/, '').trim() || item.message;
                  return (
                    <tr key={item.id} className={`border-t border-gray-100 align-top ${resolved ? 'bg-white' : 'bg-red-50/40'}`}>
                      <td className="px-4 py-3 font-semibold text-gray-900">{item.name}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{item.phone || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 break-all">{item.email || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{item.company || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-100">
                          {item.service_name || 'Partner'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700 max-w-xs whitespace-pre-wrap">{written || '—'}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatWhen(item.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          <a href={mailHref(item)} className="px-2.5 py-1.5 rounded-lg bg-brand-50 text-brand-700 text-xs font-semibold">Email</a>
                          <a href={whatsappHref(item)} target="_blank" rel="noreferrer" className="px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold">WhatsApp</a>
                          {resolved ? (
                            <button type="button" onClick={() => setEnquiryStatus(item, 'new')} className="px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold">Reopen</button>
                          ) : (
                            <button type="button" onClick={() => setEnquiryStatus(item, 'resolved')} className="px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold">Resolve</button>
                          )}
                          <button type="button" onClick={() => removeEnquiry(item.id)} className="px-2.5 py-1.5 rounded-lg text-red-500 text-xs font-semibold">Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
        ) : null}

        {page === 'partners' ? (
        <section className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-display text-2xl font-extrabold text-gray-900 mb-1">Trusted partners</h2>
          <p className="text-sm text-gray-500 mb-2">These names fill the two moving rows on the home page. Hide, delete or add — new names stay in those two rows.</p>
          <p className="text-sm font-semibold text-gray-700 mb-6">{partners.length} partners listed</p>

          <form onSubmit={addPartner} className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                className="input-field"
                placeholder="Partner name"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Website (optional)</label>
              <input
                value={form.link}
                onChange={(e) => setForm((prev) => ({ ...prev, link: e.target.value }))}
                className="input-field"
                placeholder="https://"
              />
            </div>
            <div className="flex gap-2">
              <label className="flex-1">
                <span className="block text-sm font-semibold text-gray-700 mb-1">Logo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setForm((prev) => ({ ...prev, logo: e.target.files?.[0] || null }))}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-brand-50 file:text-brand-700"
                />
              </label>
              <button type="submit" disabled={saving} className="btn-primary text-white h-[48px] mt-6 px-5 inline-flex items-center gap-2">
                {saving ? <SpinnerGap className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" weight="bold" />}
                Add
              </button>
            </div>
          </form>
          {partnerError && <p className="text-sm text-red-600 mb-4">{partnerError}</p>}
          {loading && <p className="text-sm text-gray-500 mb-4">Loading partners…</p>}

          {partners.length === 0 && !loading ? (
            <p className="text-gray-500 text-sm">No partners yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-gray-100">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold">#</th>
                    <th className="px-4 py-3 font-semibold">Logo</th>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Website</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((item, index) => (
                    <tr key={item.id} className={`border-t border-gray-100 ${item.is_active === false ? 'bg-slate-50' : 'bg-white'}`}>
                      <td className="px-4 py-3 text-gray-400">{index + 1}</td>
                      <td className="px-4 py-3">
                        {item.logo_url ? (
                          <img src={item.logo_url} alt="" className="h-12 w-12 object-contain rounded bg-gray-50" />
                        ) : (
                          <div className="h-12 w-12 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xl">
                            {item.fallback && item.fallback !== 'building' ? item.fallback : <ImageIcon className="w-5 h-5" />}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900 whitespace-normal break-words">{item.name}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 break-all max-w-xs">{item.link || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${item.is_active === false ? 'bg-gray-100 text-gray-500' : 'bg-emerald-50 text-emerald-700'}`}>
                          {item.is_active === false ? 'Hidden' : 'Visible'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button type="button" onClick={() => togglePartner(item)} className="text-gray-500 hover:text-brand-600" aria-label={item.is_active === false ? 'Show partner' : 'Hide partner'}>
                            {item.is_active === false ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                          <button type="button" onClick={() => removePartner(item.id)} className="text-red-500 hover:text-red-600" aria-label="Remove partner">
                            <Trash className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        ) : null}
      </main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-gray-100 app-bottom-nav">
        <div className="grid grid-cols-6">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = isNavActive(item.id);
            const short = item.id === 'enquiries' ? 'Inbox' : item.id === 'freelancers' ? 'Freelance' : item.label;
            return (
              <NavLink
                key={item.id}
                to={item.to || `/admin/${item.id}`}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold ${
                  active ? 'text-brand-600' : 'text-gray-400'
                }`}
              >
                <Icon className="w-5 h-5" weight={active ? 'fill' : 'duotone'} />
                {short}
              </NavLink>
            );
          })}
        </div>
      </nav>
      </div>
    </div>
  );
};

export default AdminDashboard;
